<script>
import { mapActions } from "vuex";
import { formatHashrate } from "@/utils";

export default {
  props: {
    // The ID of the alert whose event heatmap should be shown.
    // If `alertId` is set, `groupId` should not be and vice versa.
    alertId: {
      type: String,
      default: null,
    },

    // The ID of the group whose event heatmap should be shown.
    // If `groupId` is set, `alertId` should not be and vice versa.
    groupId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      // The data to display
      data: null,

      // The start time of the chart
      start: null,

      // The end time of the chart
      end: null,

      // The maximum number of alerts shown on the chart
      max: null,
    };
  },

  computed: {
    // Compute the timestamp of the first day of each month
    // shown on the chart, to use as labels
    firstOfEachMonth() {
      let month = new Date(this.start).getMonth();
      let year = new Date(this.start).getYear();
      let date = new Date(year, month);
      let months = [];
      while (date < new Date(this.end)) {
        month += 1;
        year += Math.floor(month / 12);
        month = month % 12;
        date = new Date(year, month);
        months.push(date.getTime());
      }
      return months;
    },

    // Determine the chart options.
    chartOptions() {
      return {
        title: false,
        legend: false,
        xAxis: {
          type: "date",
          min: this.start,
          max: this.end,
          labels: {
            align: "left",
            x: 5,
            y: 14,
            format: "{value:%B}",
          },
          showLastLabel: true,
          tickLength: 16,
          tickPositions: this.firstOfEachMonth,
        },
        yAxis: {
          title: {
            text: null,
          },
          labels: {
            y: -5,
            x: 0,
            align: "left",
            formatter() {
              let value = this.value * 3;
              let displayValue = value % 12 || 12;
              return value + 1 > 12
                ? `${displayValue} PM`
                : `${displayValue} AM`;
            },
            style: {
              textOutline: "2px rgba(255,255,255,0.6)",
            },
          },
          minPadding: 0,
          maxPadding: 0.02,
          startOnTick: false,
          endOnTick: false,
          tickPositions: [0, 2, 4, 6],
          min: 0,
          max: 7,
          reversed: true,
        },
        colorAxis: {
          stops: [
            [0.0, "#ffffff"],
            [0.5, "#c4463a"],
            [1, "#c4463a"],
          ],
          min: 0,
          max: this.max,
          startOnTick: false,
          endOnTick: false,
          labels: false,
        },
        tooltip: {
          borderRadius: 8,
          borderWidth: 1,
          shadow: false,
          shared: true,
          backgroundColor: "rgba(255,255,255,0.85)",
          borderColor: "rgba(0, 20, 40, 0.15)",
          formatter: function () {
            let threeHours = 3 * 60 * 60 * 1000;
            let offsetMs = this.point.y * threeHours;
            let start = new Date(this.point.date.getTime() + offsetMs);
            let end = new Date(start.getTime() + threeHours);
            return `${start.toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "numeric",
            })} - ${end.toLocaleTimeString(undefined, {
              hour: "numeric",
            })}
              <br/>
              ${this.point.value} alert${
              this.point.value == 1 ? "" : "s"
            } triggered`;
          },
        },
        chart: {
          type: "heatmap",
          backgroundColor: "rgba(0,0,0,0)",
          marginTop: 10,
          marginLeft: 0,
          marginRight: 0,
          marginBottom: 16,
        },

        series: [
          {
            name: "Alert vents",
            data: this.data,
            colsize: 24 * 36e5, // one day
            color: "rgba(0, 127,255, 0.6)",
          },
        ],

        exporting: {
          enabled: false,
        },
      };
    },
  },

  async created() {
    // Get the TZ offset to use against the API
    let timezone = -new Date().getTimezoneOffset();

    // Fetch the data from the API
    let result = await this.getAlertEvents({
      alertId: this.alertId,
      groupId: this.groupId,
      format: "heatmap",
      timezone,
    });

    // Set the necessary data on the controller
    this.start = new Date(result.start).getTime();
    this.end = new Date(result.end).getTime();
    this.max = result.max;
    this.data = result.heatmap.map((point) => {
      let [year, month, day] = point.date.split("-");
      return {
        x: new Date(point.date).getTime(),
        y: point.hour_3,
        value: point.event_count,
        date: new Date(year, month - 1, day),
      };
    });
  },

  // Helper methods and state functions
  methods: {
    formatHashrate,
    ...mapActions(["getAlertEvents"]),
  },
};
</script>

<template>
  <b-card no-body class="mb-3">
    <b-card-body class="p-2 p-md-3">
      <div class="Chart">
        <highcharts :options="chartOptions" />
      </div>
    </b-card-body>
  </b-card>
</template>

<style scoped>
.Chart {
  position: relative;
  width: 100%;
  padding: calc(max(15%, 120px));
}

.Chart div {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}
</style>
